import React from 'react';

import { IS_GROUPED_LAYOUT_OFFERED } from './constants';

// Line icons for the Studio vis type palette. Drawn on a 24x24 grid in
// currentColor so tiles can tint them via CSS color.
const Svg = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);

const ICONS: Record<Studio.VisType, JSX.Element> = {
  number: (
    <Svg>
      <text
        x="12"
        y="15"
        textAnchor="middle"
        fontSize="10"
        fontWeight="600"
        fill="currentColor"
        stroke="none"
      >
        123
      </text>
      <path d="M5 19h14" />
    </Svg>
  ),
  line: (
    <Svg>
      <path d="M4 4v16h16" />
      <path d="M7 15l4-5 3 3 5-6" />
    </Svg>
  ),
  bar: (
    <Svg>
      <path d="M4 20h16" />
      <rect x="6" y="11" width="3" height="9" />
      <rect x="11" y="6" width="3" height="14" />
      <rect x="16" y="13" width="3" height="7" />
    </Svg>
  ),
  combo: (
    <Svg>
      <path d="M4 20h16" />
      <rect x="6" y="13" width="3" height="7" />
      <rect x="11" y="10" width="3" height="10" />
      <rect x="16" y="14" width="3" height="6" />
      <path d="M5 9l5-3 4 2 5-4" />
    </Svg>
  ),
  pie: (
    <Svg>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4v8l6 5" />
    </Svg>
  ),
  table: (
    <Svg>
      <rect x="4" y="5" width="16" height="14" rx="1" />
      <path d="M4 10h16M4 14.5h16M10 5v14" />
    </Svg>
  ),
  stackedBar: (
    <Svg>
      <path d="M4 20h16" />
      <rect x="6" y="8" width="3" height="12" />
      <rect x="11" y="5" width="3" height="15" />
      <rect x="16" y="11" width="3" height="9" />
      <path d="M6 14h3M11 12h3M16 15h3" />
    </Svg>
  ),
  area: (
    <Svg>
      <path d="M4 4v16h16" />
      <path d="M4 16l5-6 4 3 7-7v14H4z" fill="currentColor" fillOpacity={0.2} />
    </Svg>
  ),
  gauge: (
    <Svg>
      <path d="M4 17a8 8 0 1 1 16 0" />
      <path d="M12 17l4-5" />
      <circle cx="12" cy="17" r="1" fill="currentColor" />
    </Svg>
  ),
  bullet: (
    <Svg>
      <rect x="4" y="6" width="16" height="4" />
      <path d="M4 8h9" strokeWidth={3} />
      <path d="M16 5v6" />
      <rect x="4" y="14" width="16" height="4" />
      <path d="M4 16h13" strokeWidth={3} />
      <path d="M12 13v6" />
    </Svg>
  ),
  funnel: (
    <Svg>
      <path d="M4 5h16l-2 4H6z" />
      <path d="M7 11h10l-2 4H9z" />
      <path d="M10 17h4l-1 3h-2z" />
    </Svg>
  ),
  heatmap: (
    <Svg>
      {[5, 10, 15].map((y, row) =>
        [5, 10, 15].map((x, col) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="4"
            height="4"
            fill="currentColor"
            fillOpacity={0.15 + ((row + col) % 3) * 0.35}
            stroke="none"
          />
        )),
      )}
    </Svg>
  ),
  radar: (
    <Svg>
      <path d="M12 3l8.5 6.2-3.3 10H6.8L3.5 9.2z" />
      <path d="M12 8l4 3.5-1.6 4.5H9.6L8 11.5z" />
    </Svg>
  ),
  scatter: (
    <Svg>
      <path d="M4 4v16h16" />
      {[
        [8, 15],
        [10, 11],
        [13, 13],
        [15, 8],
        [18, 10],
        [17, 6],
      ].map(([cx, cy]) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r="1.2"
          fill="currentColor"
        />
      ))}
    </Svg>
  ),
  treeMap: (
    <Svg>
      <rect x="4" y="4" width="16" height="16" rx="1" />
      <path d="M12 4v16M12 11h8M4 14h8M16 11v9" />
    </Svg>
  ),
  waterfall: (
    <Svg>
      <path d="M4 20h16" />
      <rect x="5" y="10" width="3" height="10" />
      <rect x="9.5" y="6" width="3" height="4" />
      <rect x="14" y="6" width="3" height="6" />
      <path d="M8 10h1.5M12.5 6H14" />
    </Svg>
  ),
  packedBubble: (
    <Svg>
      <circle cx="10" cy="11" r="5" />
      <circle cx="17" cy="8" r="2.5" />
      <circle cx="16" cy="16" r="3" />
      <circle cx="7" cy="18" r="1.5" />
    </Svg>
  ),
  sankey: (
    <Svg>
      <path d="M4 5v5M4 14v5M20 4v7M20 15v5" />
      <path d="M4 7.5c8 0 8 0 16 0M4 16.5c8 0 8-9 16-9M4 9c8 0 8 8.5 16 8.5" />
    </Svg>
  ),
  pictorialBar: (
    <Svg>
      <path d="M4 20h16" />
      {[16, 11.5, 7].map((y) => (
        <path key={`a-${y}`} d={`M5 ${y}h4v2.5H5z`} />
      ))}
      {[16, 11.5].map((y) => (
        <path key={`b-${y}`} d={`M11 ${y}h4v2.5h-4z`} />
      ))}
      <path d="M17 16h2.5v2.5H17z" />
    </Svg>
  ),
  regionMap: (
    <Svg>
      <path d="M3 7l5-2 8 2 5-2v12l-5 2-8-2-5 2z" />
      <path d="M8 5v12M16 7v12" />
      <circle cx="12" cy="11" r="1.5" fill="currentColor" />
    </Svg>
  ),
  trend: (
    <Svg>
      <rect x="3" y="6" width="5" height="12" rx="1" />
      <rect x="9.5" y="6" width="5" height="12" rx="1" />
      <rect x="16" y="6" width="5" height="12" rx="1" />
      <path d="M4.5 13l1.5-2 1 1M11 13l1.5-2 1 1M17.5 13l1.5-2 1 1" />
    </Svg>
  ),
  text: (
    <Svg>
      <path d="M5 6h14M12 6v12M9 18h6" />
    </Svg>
  ),
  composite: (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M6 8h4M14 8h4" />
      <path d="M6 14l3-2 3 1 6-3" />
      <path d="M3 16.5h18M12 16.5V20" />
    </Svg>
  ),
  image: (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M3 17l5-5 4 4 3-3 6 6" />
    </Svg>
  ),
  video: (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M10 9.5v5l4.5-2.5z" />
    </Svg>
  ),
  iframe: (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 8h18" />
      <path d="M10 12l-2 2 2 2M14 12l2 2-2 2" />
    </Svg>
  ),
  classic: (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11" />
    </Svg>
  ),
};

// Each type's hover colour is a different chart palette token. Only mid-tone
// ones: the palette is shared by both themes, and its darkest shades (and
// palest yellows) would vanish on one background or the other.
const chartColor = (n: number) => `var(--color-chart-${n})`;

const ALL_VIS_TYPES: Studio.VisTypeOption[] = [
  { id: 'number', label: 'Number', gadgetType: 'card', color: chartColor(28) },
  { id: 'line', label: 'Line', gadgetType: 'timeSeries', color: chartColor(3) },
  // Sideways, a horizontal bar (ChartOptions.isHorizontal)
  { id: 'bar', label: 'Bar', gadgetType: 'bar', color: chartColor(5) },
  { id: 'combo', label: 'Combo', gadgetType: 'combo', color: chartColor(2) },
  // With a hole in the middle, a donut (ChartOptions.isDonut)
  { id: 'pie', label: 'Pie', gadgetType: 'pie', color: chartColor(8) },
  // Aggregated rows by dimension, not the raw-data simpleGrid
  { id: 'table', label: 'Table', gadgetType: 'matrix', color: chartColor(19) },
  {
    id: 'stackedBar',
    label: 'Stacked',
    gadgetType: 'stackedBar',
    color: chartColor(6),
  },
  {
    id: 'area',
    label: 'Area',
    gadgetType: 'stackedArea',
    color: chartColor(23),
  },
  // Saved as a legacy Gauge, not a V5 gadget type (see StudioGauge)
  { id: 'gauge', label: 'Gauge', color: chartColor(11) },
  // Pointed at the top, a pyramid (ChartOptions.isPyramid)
  {
    id: 'funnel',
    label: 'Funnel',
    gadgetType: 'funnel',
    color: chartColor(18),
  },
  {
    id: 'heatmap',
    label: 'Heatmap',
    gadgetType: 'heatmap',
    color: chartColor(17),
  },
  { id: 'radar', label: 'Spider', gadgetType: 'radar', color: chartColor(10) },
  // Sized by a third metric, a bubble chart
  {
    id: 'scatter',
    label: 'Scatter',
    gadgetType: 'scatter',
    color: chartColor(21),
  },
  {
    id: 'treeMap',
    label: 'Tree map',
    gadgetType: 'treeMap',
    color: chartColor(38),
  },
  {
    id: 'waterfall',
    label: 'Waterfall',
    gadgetType: 'waterfall',
    color: chartColor(40),
  },
  {
    id: 'packedBubble',
    label: 'Bubble',
    gadgetType: 'packedBubble',
    color: chartColor(42),
  },
  {
    id: 'sankey',
    label: 'Sankey',
    gadgetType: 'sankey',
    color: chartColor(39),
  },
  // Drawn with an icon, saved as a Bar (sideways, a horizontal bar): only
  // Studio's ECharts draws the icons (see ECHARTS_ONLY_VIS_TYPES)
  {
    id: 'pictorialBar',
    label: 'Pictorial',
    gadgetType: 'bar',
    color: chartColor(4),
  },
  // States and provinces shaded (or bubbled) by a metric, saved as a Bar of
  // the dimension's terms: only Studio's ECharts draws the map
  {
    id: 'regionMap',
    label: 'Map',
    gadgetType: 'bar',
    color: chartColor(24),
  },
  // A value against a target over good / OK / poor bands: a bullet per
  // metric, or per term of the first (see BulletCharts)
  {
    id: 'bullet',
    label: 'Bullet',
    gadgetType: 'bullet',
    color: chartColor(14),
  },
  // A metric's latest period against earlier ones: 30-day blocks, or
  // calendar months as a fixedTrendCard (see Card.trend)
  {
    id: 'trend',
    label: 'Trend',
    gadgetType: 'rollingTrendCard',
    color: chartColor(9),
  },
  // Static rich text, typed straight into the block
  { id: 'text', label: 'Text', color: chartColor(1) },
  // Several elements (numbers, charts) in one block, not a V5 gadget itself
  { id: 'composite', label: 'Grouped', color: chartColor(36) },
  // Media, saved as the classic Image, Video and iFrame cards
  { id: 'image', label: 'Image', color: chartColor(22) },
  { id: 'video', label: 'Video', color: chartColor(29) },
  { id: 'iframe', label: 'Embed', color: chartColor(7) },
];

// Listed (in the vis type dropdowns) by label
export const VIS_TYPES: Studio.VisTypeOption[] = [...ALL_VIS_TYPES].sort(
  (a, b) => a.label.localeCompare(b.label),
);

// The palette's sections and the types in each, the most used first
const PALETTE_SECTIONS: {
  id: string;
  label: string;
  visTypes: Studio.VisType[];
}[] = [
  {
    id: 'charts',
    label: 'Charts',
    visTypes: [
      'line',
      'bar',
      'combo',
      'pie',
      'stackedBar',
      'area',
      'funnel',
      'heatmap',
      'scatter',
      'radar',
      'treeMap',
      'waterfall',
      'packedBubble',
      'sankey',
      'pictorialBar',
      'regionMap',
    ],
  },
  {
    id: 'values',
    label: 'Values',
    visTypes: ['number', 'table', 'trend', 'gauge', 'bullet', 'text'],
  },
  // Grouped blocks aren't offered for now (see IS_GROUPED_LAYOUT_OFFERED);
  // Text is with the values it shows
  {
    id: 'layout',
    label: 'Layout',
    visTypes: [
      ...(IS_GROUPED_LAYOUT_OFFERED ? ['composite' as const] : []),
      'image',
      'video',
      'iframe',
    ],
  },
];

// In the palette's Charts section, rather than a value (a Number, Table,
// Text…) or a layout block
export const isChartVisType = (visType: Studio.VisType): boolean =>
  PALETTE_SECTIONS.some(
    (section) => section.id === 'charts' && section.visTypes.includes(visType),
  );

export const PALETTE_GROUPS: {
  id: string;
  label: string;
  visTypes: Studio.VisTypeOption[];
}[] = PALETTE_SECTIONS.map(({ id, label, visTypes }) => ({
  id,
  label,
  visTypes: visTypes.flatMap((visType) =>
    VIS_TYPES.filter((v) => v.id === visType),
  ),
}));

// An uploaded picture or video, or an embedded web page: no metrics, saved
// as the classic Image, Video and iFrame cards
export const MEDIA_VIS_TYPES: Studio.MediaVisType[] = [
  'image',
  'video',
  'iframe',
];

// A media block's file or link, cleared when it switches kind
export const MEDIA_SOURCE_KEYS = [
  'mediaPath',
  'imageLayout',
  'iFrameSnippet',
] as const;

export const isMediaVisType = (
  visType: Studio.VisType | undefined,
): visType is Studio.MediaVisType =>
  !!visType && (MEDIA_VIS_TYPES as Studio.VisType[]).includes(visType);

// Whether a Composite element can take the vis type: not another Composite,
// a Classic card or media (blocks only)
export const canBeElement = (
  visType: Studio.VisType,
): visType is Studio.ElementVisType =>
  visType !== 'composite' && visType !== 'classic' && !isMediaVisType(visType);

// Whether a media block has its file or link yet
export const hasMediaSource = (
  card: Pick<Studio.Card, 'mediaPath' | 'iFrameSnippet'>,
): boolean => !!card.mediaPath || !!card.iFrameSnippet;

// What a block of the vis type can switch to: a media block to another
// kind of media, any other block to anything but media (either would leave
// its content behind). A block with nothing in it yet, to anything.
const MEDIA_OPTIONS = VIS_TYPES.filter((v) => isMediaVisType(v.id));
const NON_MEDIA_OPTIONS = VIS_TYPES.filter((v) => !isMediaVisType(v.id));
export const getSwitchableVisTypes = ({
  visType,
  isBlank,
}: {
  visType: Studio.VisType | undefined;
  isBlank: boolean;
}): Studio.VisTypeOption[] => {
  if (isBlank) {
    return VIS_TYPES;
  }
  return isMediaVisType(visType) ? MEDIA_OPTIONS : NON_MEDIA_OPTIONS;
};

const isElementVisType = (
  option: Studio.VisTypeOption,
): option is Studio.VisTypeOption & { id: Studio.ElementVisType } =>
  canBeElement(option.id);

// What a Composite block's elements can be
export const ELEMENT_VIS_TYPES = VIS_TYPES.filter(isElementVisType);

// Vis types only Studio's ECharts renderer can draw: they always draw with
// it, and are only offered where it's on (see useVisTypeOptions). Saved as
// plain Bars, so everything else (the classic views and editor) draws a Bar.
export const ECHARTS_ONLY_VIS_TYPES: Studio.VisType[] = [
  'pictorialBar',
  'regionMap',
];

export const isEChartsOnly = (visType: Studio.VisType | undefined): boolean =>
  !!visType && ECHARTS_ONLY_VIS_TYPES.includes(visType);

// A Heatmap of its metrics over time, a row each, rather than of one metric
// across two dimensions. Only ECharts draws it: the classic heatmap needs
// both dimensions.
export const isTimelineHeatmap = (
  content: Pick<Studio.ChartContent, 'visType' | 'chart'>,
): boolean =>
  content.visType === 'heatmap' && content.chart?.heatmapMode === 'timeline';

// A Timeline heatmap's chart definition: a heatmap over dates, with no
// second dimension (a classic heatmap always has one)
export const isTimelineHeatmapDef = (chartDef: V5ChartDefinition): boolean =>
  chartDef.gadgetType === 'heatmap' && !chartDef.dimensionB;

export const DEFAULT_VIS_TYPE: Studio.VisType = 'number';

// Gadget types whose renderers pick their own colours (or draw none): the
// Number's card, Tables, and charts that colour each point themselves
const UNCOLOURED_GADGET_TYPES: GadgetType[] = [
  'card',
  'matrix',
  'treeMap',
  'waterfall',
  'sankey',
  'rollingTrendCard',
  'fixedTrendCard',
  // Coloured by its bands
  'bullet',
];

// Trend cards bucket one metric's dates themselves: no dimensions
export const isTrendCard = (visType: Studio.VisType | undefined): boolean =>
  visType === 'trend';

// Whether the block groups by dimensions: not a Number (totals), a trend
// card or a Gauge (its peer group is its own setting)
export const takesDimensions = (visType: Studio.VisType | undefined): boolean =>
  (visType ?? DEFAULT_VIS_TYPE) !== DEFAULT_VIS_TYPE &&
  visType !== 'gauge' &&
  !isTrendCard(visType);

// Whether a block's palette and metric colours reach its chart
export const takesChartColours = (visType: Studio.VisType | undefined) => {
  const { gadgetType } = getVisType(visType);
  return !!gadgetType && !UNCOLOURED_GADGET_TYPES.includes(gadgetType);
};

// The gadget type a block saves as. Some vis types save as one of two, by
// their settings: a Pie with a hole as a donut, a sideways Bar as a
// horizontal bar, a pointed Funnel as a pyramid, a Scatter sized by a third
// metric as a bubble chart, and a Trend by month as a fixed trend card.
export const getGadgetType = (
  content: Pick<
    Studio.ChartContent,
    'visType' | 'metricIds' | 'chart' | 'trend'
  >,
): GadgetType | undefined => {
  const options = content.chart ?? {};
  switch (content.visType) {
    case 'pie':
      return options.isDonut ? 'donut' : 'pie';
    case 'bar':
    case 'pictorialBar':
      return options.isHorizontal ? 'horizontalBar' : 'bar';
    case 'funnel':
      return options.isPyramid ? 'pyramid' : 'funnel';
    case 'scatter':
      return (content.metricIds ?? []).length >= 3 ? 'bubble' : 'scatter';
    case 'trend':
      return content.trend && content.trend.period === 'month'
        ? 'fixedTrendCard'
        : 'rollingTrendCard';
    default:
      return getVisType(content.visType).gadgetType;
  }
};

// The vis type each gadget type opens as, the other of a pair included.
// Not the ECharts-only types: they save as Bars, and a Bar gadget without
// Studio's settings opens as a Bar.
export const VIS_TYPE_BY_GADGET_TYPE: Partial<
  Record<GadgetType, Studio.VisType>
> = {
  ...VIS_TYPES.reduce<Partial<Record<GadgetType, Studio.VisType>>>(
    (acc, option) =>
      option.gadgetType && !isEChartsOnly(option.id)
        ? { ...acc, [option.gadgetType]: option.id }
        : acc,
    {},
  ),
  donut: 'pie',
  horizontalBar: 'bar',
  pyramid: 'funnel',
  bubble: 'scatter',
  fixedTrendCard: 'trend',
};

// Vis types Studio had before pairs were merged (see getGadgetType), as
// they're now drawn: for blocks, elements and drafts saved with them
type ContentWithVisType = {
  visType?: Studio.VisType;
  chart?: Studio.ChartOptions;
  trend?: Studio.TrendSettings;
};
export const withCurrentVisType = <T extends ContentWithVisType>(
  content: T,
): T => {
  const chart = content.chart ?? {};
  switch (content.visType as string | undefined) {
    case 'donut':
      return { ...content, visType: 'pie', chart: { ...chart, isDonut: true } };
    case 'horizontalBar':
      return {
        ...content,
        visType: 'bar',
        chart: { ...chart, isHorizontal: true },
      };
    case 'pyramid':
      return {
        ...content,
        visType: 'funnel',
        chart: { ...chart, isPyramid: true },
      };
    case 'bubble':
      return { ...content, visType: 'scatter' };
    case 'rollingTrend':
      return { ...content, visType: 'trend' };
    case 'monthlyTrend':
      return { ...content, visType: 'trend', trend: { period: 'month' } };
    default:
      return content;
  }
};

export const getVisType = (
  id: Studio.VisType | undefined,
): Studio.VisTypeOption =>
  VIS_TYPES.find((v) => v.id === (id ?? DEFAULT_VIS_TYPE)) ??
  VIS_TYPES.find((v) => v.id === DEFAULT_VIS_TYPE) ??
  VIS_TYPES[0];

export const VisTypeIcon = ({ visType }: { visType: Studio.VisType }) =>
  ICONS[visType];
